import { humanizeEnum } from '@/lib/utils/format';

// User Roles (matching backend EUserRole)

export enum EUserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  IT_ADMINISTRATOR = 'IT_ADMINISTRATOR',
  // IT Administrator without the Administration menu. See
  // sidebar-config.ts for what that excludes, and the backend's
  // administration-permissions.ts for the permissions it does not hold.
  MINING_CLERK = 'MINING_CLERK',
  MINE_DIRECTOR = 'MINE_DIRECTOR',
  MINE_MANAGER = 'MINE_MANAGER',
  MINE_ENGINEER = 'MINE_ENGINEER',
  // SRS 7.7 — Zone Engineer authors zone-scoped wage / commission drafts.
  ZONE_ENGINEER = 'ZONE_ENGINEER',
  JUNIOR_SAFETY_ENGINEER = 'JUNIOR_SAFETY_ENGINEER',
  SENIOR_SAFETY_ENGINEER = 'SENIOR_SAFETY_ENGINEER',
  ENVIRONMENTALIST = 'ENVIRONMENTALIST',
  STOCK_MANAGER = 'STOCK_MANAGER',
  FINANCE_OFFICER = 'FINANCE_OFFICER',
  FIELD_SUPERVISOR = 'FIELD_SUPERVISOR',
  SUBCONTRACTOR = 'SUBCONTRACTOR',
  GOVERNMENT_INSPECTOR = 'GOVERNMENT_INSPECTOR',
}

/** @deprecated Use EUserRole instead */
export enum UserRole {
  ADMIN = 'ADMIN',
  USER = 'USER',
}

export type Portal = 'super-admin' | 'admin' | 'ops' | 'external';

export const ROLE_PORTAL_MAP: Record<EUserRole, Portal> = {
  [EUserRole.SUPER_ADMIN]: 'super-admin',
  [EUserRole.IT_ADMINISTRATOR]: 'admin',
  [EUserRole.MINING_CLERK]: 'admin',
  [EUserRole.MINE_DIRECTOR]: 'admin',
  [EUserRole.MINE_MANAGER]: 'admin',
  [EUserRole.MINE_ENGINEER]: 'ops',
  [EUserRole.ZONE_ENGINEER]: 'ops',
  [EUserRole.JUNIOR_SAFETY_ENGINEER]: 'ops',
  [EUserRole.SENIOR_SAFETY_ENGINEER]: 'ops',
  [EUserRole.ENVIRONMENTALIST]: 'ops',
  [EUserRole.STOCK_MANAGER]: 'ops',
  [EUserRole.FINANCE_OFFICER]: 'ops',
  [EUserRole.FIELD_SUPERVISOR]: 'ops',
  [EUserRole.SUBCONTRACTOR]: 'external',
  [EUserRole.GOVERNMENT_INSPECTOR]: 'external',
};

export const ROLE_DISPLAY_NAMES: Record<EUserRole, string> = {
  [EUserRole.SUPER_ADMIN]: 'Super Admin',
  [EUserRole.IT_ADMINISTRATOR]: 'IT Administrator',
  [EUserRole.MINING_CLERK]: 'Mining Clerk',
  [EUserRole.MINE_DIRECTOR]: 'Mine Director',
  [EUserRole.MINE_MANAGER]: 'Mine Manager',
  [EUserRole.MINE_ENGINEER]: 'Mine Engineer',
  [EUserRole.ZONE_ENGINEER]: 'Zone Engineer',
  [EUserRole.JUNIOR_SAFETY_ENGINEER]: 'Jr. Safety Engineer',
  [EUserRole.SENIOR_SAFETY_ENGINEER]: 'Sr. Safety Engineer',
  [EUserRole.ENVIRONMENTALIST]: 'Environmentalist',
  [EUserRole.STOCK_MANAGER]: 'Stock Manager',
  [EUserRole.FINANCE_OFFICER]: 'Finance Officer',
  [EUserRole.FIELD_SUPERVISOR]: 'Field Supervisor',
  [EUserRole.SUBCONTRACTOR]: 'Subcontractor',
  [EUserRole.GOVERNMENT_INSPECTOR]: 'Govt. Inspector',
};

/** Shown where a person has no role the platform can name. */
export const NO_ROLE_LABEL = 'No role assigned';

/**
 * Tenants can define custom roles beyond the fixed EUserRole set, so a lookup
 * in ROLE_DISPLAY_NAMES is never guaranteed to hit. Falls back to humanising
 * the raw role name (`shift_supervisor` → `Shift Supervisor`) so callers always
 * get a printable label.
 *
 * A user can genuinely hold no role: `user.role` is nullable and a user need
 * not appear in `user_roles`, so the personnel directory returns null for
 * them. Callers used to reach straight for `.replace()` on that null and take
 * the whole page down with them, so this accepts a missing role and names it.
 */
export function getRoleDisplayName(roleName: string | null | undefined): string {
  if (!roleName) return NO_ROLE_LABEL;
  const key = roleName.toUpperCase();
  const known = (ROLE_DISPLAY_NAMES as Record<string, string | undefined>)[key];
  return known ?? humanizeEnum(roleName);
}

// User & Auth

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  NOT_VERIFIED = 'NOT_VERIFIED',
  /**
   * Invited and emailed a temporary password, but has never chosen one of
   * their own. The account works — this says where the person is in
   * onboarding. Setting a password makes them ACTIVE.
   */
  PENDING_INVITATION = 'PENDING_INVITATION',
}

/**
 * What a status is called on screen. Exhaustive on purpose: until this map
 * existed, user status was the only status in the app without one, and five
 * separate `status === ACTIVE ? … : …` ternaries printed the raw enum string
 * for everything else. A new value compiled clean and every one of them
 * quietly showed the wrong thing. Now it is a build error until it is named.
 */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: 'Active',
  [UserStatus.INACTIVE]: 'Inactive',
  [UserStatus.SUSPENDED]: 'Suspended',
  [UserStatus.NOT_VERIFIED]: 'Not verified',
  [UserStatus.PENDING_INVITATION]: 'Pending invitation',
};

/** The badge each status wears. Exhaustive for the same reason as the labels. */
export const USER_STATUS_BADGE_VARIANT: Record<
  UserStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  [UserStatus.ACTIVE]: 'default',
  [UserStatus.INACTIVE]: 'secondary',
  [UserStatus.SUSPENDED]: 'destructive',
  [UserStatus.NOT_VERIFIED]: 'secondary',
  // Outline, not secondary: an invitation in flight is waiting on someone,
  // which is a different thing from an account that has been switched off.
  [UserStatus.PENDING_INVITATION]: 'outline',
};

/** Every status, in the order the filter dropdowns offer them. */
export const USER_STATUS_ORDER: readonly UserStatus[] = [
  UserStatus.ACTIVE,
  UserStatus.PENDING_INVITATION,
  UserStatus.INACTIVE,
  UserStatus.SUSPENDED,
  UserStatus.NOT_VERIFIED,
];

export enum EUserType {
  PLATFORM_ADMIN = 'PLATFORM_ADMIN',
  TENANT_USER = 'TENANT_USER',
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: EUserRole;
  roles: EUserRole[];
  status: UserStatus;
  profilePicture?: string;
  phone?: string;
  tenantId: string;
  tenantName: string;
  mustChangePassword?: boolean;
  /** When an invitation email for this account last reached the queue. */
  invitationSentAt?: string | null;
  assignedSiteId?: string;
  departmentId?: string;
  departmentName?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Effective permission names (`resource.action`) granted via the user's
   * active roles. `['*']` for super admins. Returned by login and
   * `/auth/get-profile` and used for client-side UI gating.
   */
  permissions?: string[];
}

export interface Department {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  createdById: string | null;
  employeeCount?: number;
  createdAt: string;
  updatedAt: string;
}

// A leadership position — organisational reporting structure, NOT a system
// role. `roleId` is an optional, display-only reference; it never grants or
// changes permissions. Not to be confused with the unrelated physical
// Site/Zone/Level/Tunnel "hierarchy" used elsewhere in operations.
export interface LeadershipNode {
  id: string;
  tenantId: string;
  siteId: string | null;
  roleId: string | null;
  title: string;
  departmentId: string | null;
  userId: string | null;
  reportsToId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Nested organisation-chart shape returned by GET /leadership-hierarchy/tree.
export interface LeadershipTreeNode {
  id: string;
  tenantId: string;
  siteId: string | null;
  siteName: string | null;
  roleId: string | null;
  roleName: string | null;
  title: string;
  departmentId: string | null;
  departmentName: string | null;
  userId: string | null;
  userDisplayName: string | null;
  userEmail: string | null;
  reportsToId: string | null;
  sortOrder: number;
  isActive: boolean;
  children: LeadershipTreeNode[];
}

export interface ReportingLineEntry {
  id: string;
  title: string;
  siteId: string | null;
  departmentId: string | null;
  isActive: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface ApiResponse<T = unknown> {
  data: T;
  message: string;
  statusCode: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}
